import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Box, Typography, Alert, Paper, Stack } from '@mui/material'
import { Logout } from '@mui/icons-material'
import PasswordChangeForm from '../components/auth/PasswordChangeForm'
import { authService } from '../services/authService'

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState(false)

  // 初回ログインかどうかの判定
  const isFirstLogin = location.state?.isFirstLogin || false

  useEffect(() => {
    // ログインしていない場合はログインページにリダイレクト
    if (!authService.isAuthenticated()) {
      navigate('/login')
      return
    }
    // 任意のパスワード変更を許可（アクセス制限を削除）
  }, [navigate])

  // パスワード変更処理
  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    setLoading(true)
    setError('')

    try {
      const response = await authService.changePassword(currentPassword, newPassword)

      if (response.success) {
        setSuccess(true)

        // 成功メッセージ表示後、ダッシュボードにリダイレクト
        setTimeout(() => {
          if (isFirstLogin) {
            navigate('/', { replace: true })
          } else {
            navigate('/profile', { replace: true })
          }
        }, 2000)
      } else {
        setError(response.message || 'パスワード変更に失敗しました')
      }
    } catch (error: any) {
      console.error('Password change error:', error)

      // エラーメッセージの詳細化
      if (error.response?.data?.error) {
        setError(error.response.data.error)
      } else if (error.response?.status === 400) {
        setError('現在のパスワードが正しくないか、新しいパスワードが要件を満たしていません')
      } else if (error.response?.status === 401) {
        setError('認証に失敗しました。再度ログインしてください')
        // 認証エラーの場合はログアウトしてログインページへ
        authService.logout()
        navigate('/login')
      } else if (error.response?.status === 429) {
        setError('試行回数が多すぎます。しばらく待ってから再試行してください')
      } else if (error.message) {
        setError(error.message)
      } else {
        setError('ネットワークエラーが発生しました。再試行してください')
      }
    } finally {
      setLoading(false)
    }
  }

  // ログアウト処理（初回ログインの場合のみ表示）
  const handleLogout = () => {
    authService.logout()
    navigate('/login')
  }

  // 成功画面の表示
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 500,
            mx: 'auto',
          }}
        >
          <Stack spacing={3}>
            <Typography variant="h5" color="primary.main">
              ✓ パスワード変更が完了しました
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isFirstLogin ? 'ダッシュボードに移動します...' : 'プロフィール画面に戻ります...'}
            </Typography>
          </Stack>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 4, md: 8 }, mb: 4 }}>
      {/* ヘッダー情報（初回ログインの場合） */}
      {isFirstLogin && (
        <Box sx={{ mb: 4 }}>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  セキュリティ設定が必要です
                </Typography>
                <Typography variant="body2">
                  初回ログインのため、パスワードの変更が必要です。
                </Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography
                  variant="caption"
                  color="primary"
                  onClick={handleLogout}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Logout fontSize="small" />
                  ログアウト
                </Typography>
              </Box>
            </Stack>
          </Alert>
        </Box>
      )}

      {/* パスワード変更フォーム */}
      <PasswordChangeForm
        onSubmit={handlePasswordChange}
        loading={loading}
        error={error}
        isFirstLogin={isFirstLogin}
      />

      {/* 追加情報（初回ログインの場合） */}
      {isFirstLogin && (
        <Box sx={{ mt: 3 }}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="h6" gutterBottom color="primary">
              パスワード設定のガイドライン
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                • 8文字以上で設定してください
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 大文字と小文字を組み合わせてください
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 数字を含めてください
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 記号（!@#$%など）を含めることを推奨します
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 推測されやすいパスワード（誕生日、名前など）は避けてください
              </Typography>
            </Stack>
          </Paper>
        </Box>
      )}
    </Container>
  )
}

export default ChangePasswordPage
